import { useMemo } from 'react';

import { Column, ColumnConfig } from '@ant-design/plots';
import styled, { useTheme } from 'styled-components';
import { useProducts } from 'features/products';
import { Flex, Section, Typography } from 'components';
import { useBreakpoints } from 'app/hooks';

interface DataItem {
  name: string;
  value: number;
}

const Legend = styled(Typography)`
  position: relative;

  &::before {
    content: '';
    display: block;
    width: 10px;
    height: 10px;

    background: ${(props: any) => {
      if (props['data-status'] === 'late') {
        return props.theme.colors.warning_200;
      }

      return props.theme.colors.primary_400;
    }};

    position: absolute;
    left: -15px;
    top: 50%;
    margin-top: -5px;
    border-radius: 5px;
  }
`;

const LearningChart = () => {
  const { colors } = useTheme();
  const breakpoint = useBreakpoints();

  const { productDetails } = useProducts();

  const chartData = useMemo<DataItem[]>(() => {
    return productDetails?.journey?.areas
      ?.filter((area) => !!area.cards.length)
      .map((area) => ({
        name: area.name,
        value: area.completionPercentage || 0,
      }));
  }, [productDetails]);

  const maxWidth = useMemo(() => {
    if (breakpoint === 'desktop') {
      return 1180;
    } else if (breakpoint === 'tablet') {
      return 580;
    }

    return 280;
  }, [breakpoint]);

  const config: ColumnConfig = {
    data: chartData,
    isStack: true,
    xField: 'name',
    yField: 'value',
    seriesField: 'type',

    height: 180,

    yAxis: {
      grid: {
        line: {
          style: {
            stroke: colors.dark_150,
            lineWidth: 1,
            lineDash: [4],
          },
        },
      },
    },

    meta: {
      value: {
        min: 0,
        max: 100,
      },
    },

    tooltip: {
      showTitle: false,
      formatter: (datum) => {
        return { name: datum.name, value: datum.value + '%' };
      },
    },

    interactions: [{ type: 'element-active' }],

    columnStyle: {
      radius: 4,
    },

    maxColumnWidth: 120,

    columnBackground: {
      style: {
        fill: colors.black80,
        radius: 4,
      },
    },

    color: ({ type }) => {
      return colors.primary_400;
    },

    style: {
      maxWidth,
    },
  };

  return (
    <Flex bg="dark_350" pt={{ m: 5, d: 10 }}>
      <Section data-mine flexDirection="column">
        <Flex justifyContent="space-between" mb={4}>
          <Typography type="type4" fontWeight="bold" color="white" upper>
            Sua jornada de aprendizagem
          </Typography>

          <Flex flexGrow={0} alignItems="center" mt={{ m: 2, d: 0 }}>
            <Legend type="type1" color="white">
              Status: em dia
            </Legend>

            <Legend type="type1" color="white" data-status="late" ml={6}>
              Status: atrasado
            </Legend>
          </Flex>
        </Flex>

        <Column {...config} />
      </Section>
    </Flex>
  );
};

export default LearningChart;
